import classNames from 'classnames';
import * as _ from 'lodash-es';
import type { HTMLAttributes, JSX } from 'react';
import React, {
  Suspense,
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { Outlet, useLocation } from 'react-router';
import AppBanner from '../../banners/AppBanner';
import ModalContainer from '../../components/ModalContainer';
import ToastContainer from '../../components/ToastContainer';
import VersionMismatchModal from '../../error/VersionMismatchModal';
import { UiThemeSetting } from '../../graphql/generated/graphql';
import OnboardingContainer from '../../onboarding/OnboardingContainer';
import { SIDEBAR_SHIFT_BREAKPOINT } from '../../post/PostLayoutSizes';
import PageMeta from '../../utils/PageMeta';
import { isElectron } from '../../utils/desktopClient';
import { setUpThemeRoot } from '../../utils/themeHelpers';
import useIsDarkMode from '../../utils/useIsDarkMode';
import { useAppDispatch } from '../redux/useAppSelector';
import * as styles from './PageLayout.module.scss';
import RightPanel from './RightPanel';
import SidebarOpenStateContext from './SidebarOpenStateContext';
import { useShouldShiftContentForSidebar } from './SidebarShiftStateProvider';
import { TOPBAR_NODE_ID } from './constants';
import useGetLayoutBaseWidth from './useGetLayoutBaseWidth';

export const SIDEBAR_TRANSITION_DURATION = parseInt(
  styles.sidebarTransitionDuration,
  10,
);

export interface PageLayoutElements {
  sidebar?: JSX.Element;
}

interface PageLayoutProps {
  elements: PageLayoutElements;
  routeName: string;
}

const Root = ({ elements: { sidebar }, routeName }: PageLayoutProps) => {
  const isDarkMode = useIsDarkMode();

  useLayoutEffect(() => {
    if (isElectron) {
      window.desktop?.changeTheme?.(
        isDarkMode ? UiThemeSetting.Dark : UiThemeSetting.Light,
      );
    }

    return setUpThemeRoot(isDarkMode);
  }, [isDarkMode]);

  const dispatch = useAppDispatch();

  const shouldShiftContentForSidebar = useShouldShiftContentForSidebar();
  const getLayoutBaseWidth = useGetLayoutBaseWidth();

  const [isSidebarOpenPreferred, setIsSidebarOpenPreferred] = useState(
    () => getLayoutBaseWidth() >= SIDEBAR_SHIFT_BREAKPOINT,
  );

  const isSidebarOpen = isSidebarOpenPreferred && !!sidebar;

  const location = useLocation();

  useEffect(() => {
    if (sidebar && getLayoutBaseWidth() < SIDEBAR_SHIFT_BREAKPOINT) {
      setIsSidebarOpenPreferred(false);
    }
  }, [sidebar, location, dispatch, getLayoutBaseWidth]);

  const toggleSidebar = useCallback(
    (forceValue?: boolean) =>
      setIsSidebarOpenPreferred(forceValue ?? ((current) => !current)),
    [],
  );

  const sidebarOpenStateValue = useMemo(
    () => ({ isSidebarOpen, toggleSidebar }),
    [isSidebarOpen, toggleSidebar],
  );

  return (
    <SidebarOpenStateContext.Provider value={sidebarOpenStateValue}>
      <div
        id={`${routeName}-view`}
        className={classNames('page-layout-root', {
          [styles.isSidebarOpen]: isSidebarOpen,
          [styles.shouldShiftContentForSidebar]: shouldShiftContentForSidebar,
        })}
      >
        <PageMeta title={`${_.startCase(routeName)} - Slab`} />
        <AppBanner />
        <ModalContainer />
        <VersionMismatchModal />
        <ToastContainer />
        {sidebar}
        <main className={classNames('page-layout-main', styles.main)}>
          <Suspense fallback={null}>
            <RightPanel.Root />
            <Outlet />
          </Suspense>
        </main>
        <div
          className={styles.overlay}
          onClick={() => {
            if (isSidebarOpen) {
              setIsSidebarOpenPreferred(false);
            }
          }}
        />
        <OnboardingContainer />
      </div>
    </SidebarOpenStateContext.Provider>
  );
};

interface SidebarProps extends HTMLAttributes<HTMLElement> {
  publicVariant?: boolean;
}

const Sidebar = ({ publicVariant, className, ...props }: SidebarProps) => (
  <nav
    className={classNames(styles.sidebar, className, {
      [styles.isPublicVariant]: publicVariant,
    })}
    {...props}
  />
);

const Topbar = forwardRef<
  HTMLHeadingElement,
  Omit<HTMLAttributes<HTMLHeadingElement>, 'id' | 'role' | 'aria-label'>
>(({ className, ...props }, ref) => (
  <header
    ref={ref}
    id={TOPBAR_NODE_ID}
    role="banner"
    aria-label="Topbar"
    className={classNames(styles.topbar, className)}
    {...props}
  />
));

interface ContentProps extends Omit<HTMLAttributes<HTMLDivElement>, 'id'> {
  noPaddingX?: boolean;
  noPaddingY?: boolean;
}

const Content = ({
  noPaddingX,
  noPaddingY,
  className,
  ...props
}: ContentProps) => (
  <div
    id="content"
    className={classNames(styles.content, className, {
      [styles.noPaddingX]: noPaddingX,
      [styles.noPaddingY]: noPaddingY,
    })}
    {...props}
  />
);

const Inner = ({ children, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={classNames('page-layout-inner', styles.inner)} {...props}>
    {children}
    <RightPanel.PinnedPlaceholder />
  </div>
);

export default { Content, Inner, Root, Sidebar, Topbar };
